<template>
  <section class="grid grid-cols-1 md:grid-cols-2 gap-3">
    <!-- Source Wallet (EVM or Solana) -->
    <article
      class="rounded-2xl border p-4 transition-colors"
      :class="sourceWalletAddress ? 'border-[#1980e6]/50 bg-[#1980e6]/5' : disconnectBtnBorderClass"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-xl flex items-center justify-center"
            :class="sourceChainType === 'solana' ? 'bg-[#9945FF]/10' : 'bg-[#627EEA]/10'"
          >
            <svg
              v-if="sourceChainType === 'solana'"
              class="w-4 h-4 text-[#9945FF]"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M4.5 19.5h13.2l2.8-2.8H7.3L4.5 19.5zM4.5 13.4h13.2l2.8-2.8H7.3L4.5 13.4zM7.3 4.5L4.5 7.3h13.2l2.8-2.8H7.3z"
              />
            </svg>
            <svg v-else class="w-4 h-4 text-[#627EEA]" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z"
              />
            </svg>
          </div>
          <span class="text-sm font-semibold text-[#0e141b]">{{
            sourceChainType === 'solana' ? 'Solana Wallet' : 'EVM Wallet'
          }}</span>
        </div>
        <span v-if="sourceWalletAddress" class="flex items-center gap-1 text-xs text-[#1980e6]">
          <span class="w-2 h-2 rounded-full bg-[#1980e6] animate-pulse" />
          Connected
        </span>
      </div>

      <p class="text-xs text-[#4e7397] mb-3 font-mono truncate">
        {{ sourceWalletAddress || 'Connect to select tokens you own' }}
      </p>

      <div v-if="!sourceWalletAddress" class="flex flex-col gap-2">
        <button
          v-if="showPhantomDeeplinkButton"
          type="button"
          class="w-full px-4 py-2.5 rounded-xl bg-[#AB9FF2] text-white text-sm font-semibold hover:bg-[#9A8DE0] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          :disabled="isSourceBusy"
          @click="$emit('connect-source', 'wallet_panel')"
        >
          <!-- Official Phantom ghost mark from docs.phantom.com/resources/assets. -->
          <svg class="w-5 h-5" viewBox="0 0 128 128" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M55.6416 82.1477C50.8744 89.4525 42.8862 98.6966 32.2568 98.6966C27.232 98.6966 22.4004 96.628 22.4004 87.6424C22.4004 64.7584 53.6445 29.3335 82.6339 29.3335C99.1257 29.3335 105.697 40.7755 105.697 53.7689C105.697 70.4471 94.8739 89.5171 84.1156 89.5171C80.7013 89.5171 79.0264 87.6424 79.0264 84.6688C79.0264 83.8931 79.1552 83.0527 79.4129 82.1477C75.7409 88.4182 68.6546 94.2361 62.0192 94.2361C57.1877 94.2361 54.7397 91.1979 54.7397 86.9314C54.7397 85.3799 55.0618 83.7638 55.6416 82.1477ZM80.6133 53.3182C80.6133 57.1044 78.3795 58.9975 75.8806 58.9975C73.3438 58.9975 71.1479 57.1044 71.1479 53.3182C71.1479 49.532 73.3438 47.6389 75.8806 47.6389C78.3795 47.6389 80.6133 49.532 80.6133 53.3182ZM94.8102 53.3184C94.8102 57.1046 92.5763 58.9977 90.0775 58.9977C87.5407 58.9977 85.3447 57.1046 85.3447 53.3184C85.3447 49.5323 87.5407 47.6392 90.0775 47.6392C92.5763 47.6392 94.8102 49.5323 94.8102 53.3184Z"
            />
          </svg>
          {{ isSourceBusy ? 'Opening Phantom…' : 'Connect with Phantom' }}
        </button>

        <button
          v-if="showFallbackSourceButton"
          type="button"
          class="w-full px-4 py-2.5 rounded-xl bg-[#1980e6] text-white text-sm font-medium hover:bg-[#166ec5] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
          :disabled="isSourceBusy"
          @click="$emit('connect-source', 'wallet_panel')"
        >
          <svg v-if="isSourceBusy" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          {{ sourceConnectLabel }}
        </button>
      </div>

      <button
        v-else
        type="button"
        class="w-full px-4 py-2.5 rounded-xl border text-sm font-medium text-[#4e7397] hover:text-[#0e141b] hover:border-gray-400 transition-colors"
        :class="disconnectBtnClass"
        @click="$emit('disconnect-source')"
      >
        Disconnect
      </button>
    </article>

    <!-- TON Wallet -->
    <article
      class="rounded-2xl border p-4 transition-colors"
      :class="tonAddress ? 'border-[#1980e6]/50 bg-[#1980e6]/5' : disconnectBtnBorderClass"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-xl bg-[#0088cc]/10 flex items-center justify-center">
            <svg class="w-5 h-5" viewBox="0 0 56 56" fill="none">
              <path
                d="M28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56Z"
                fill="#0098EA"
              />
              <path
                d="M37.5603 15.6277H18.4386C14.9228 15.6277 12.6944 19.4202 14.4632 22.4861L26.2644 42.9409C27.0345 44.2765 28.9644 44.2765 29.7345 42.9409L41.5381 22.4861C43.3045 19.4251 41.0761 15.6277 37.5627 15.6277H37.5603ZM26.2548 36.8068L23.6847 31.8327L17.4833 20.7414C17.0742 20.0315 17.5795 19.1218 18.4362 19.1218H26.2524V36.8092L26.2548 36.8068ZM38.5108 20.739L32.3118 31.8351L29.7417 36.8068V19.1194H37.5579C38.4146 19.1194 38.9199 20.0291 38.5108 20.739Z"
                fill="white"
              />
            </svg>
          </div>
          <span class="text-sm font-semibold text-[#0e141b]">TON Wallet</span>
        </div>
        <span
          v-if="tonAddress && !needsTonReconnect"
          class="flex items-center gap-1 text-xs text-[#1980e6]"
        >
          <span class="w-2 h-2 rounded-full bg-[#1980e6] animate-pulse" />
          Connected
        </span>
        <span v-else-if="needsTonReconnect" class="text-xs font-medium text-amber-700">
          Reconnect required
        </span>
      </div>

      <p class="text-xs text-[#4e7397] mb-3 font-mono truncate">
        {{ tonAddress || 'Required for receiving SPOTTY' }}
      </p>

      <button
        v-if="!tonAddress || needsTonReconnect"
        type="button"
        class="w-full px-4 py-2.5 rounded-xl bg-[#1980e6] text-white text-sm font-medium hover:bg-[#166ec5] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
        :disabled="isTonBusy"
        @click="$emit('connect-ton')"
      >
        <svg v-if="isTonBusy" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        {{
          isTonBusy
            ? 'Connecting...'
            : needsTonReconnect
              ? 'Reconnect TON Wallet'
              : 'Connect TON Wallet'
        }}
      </button>

      <button
        v-else
        type="button"
        class="w-full px-4 py-2.5 rounded-xl border text-sm font-medium text-[#4e7397] hover:text-[#0e141b] hover:border-gray-400 transition-colors"
        :class="disconnectBtnClass"
        @click="$emit('disconnect-ton')"
      >
        Disconnect
      </button>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  sourceChainType: 'evm' | 'solana'
  sourceWalletAddress: string | null | undefined
  tonAddress: string | null | undefined
  needsTonReconnect?: boolean
  isSourceBusy: boolean
  isTonBusy: boolean
  disconnectBtnBorderClass: string
  disconnectBtnClass: string
  // True on mobile web without an injected Phantom provider — the only
  // environment where our encrypted deeplink path is reachable. The parent
  // passes this from isPhantomDeeplinkEnvironment() so tests can stub it.
  phantomDeeplinkAvailable?: boolean
}>()

defineEmits<{
  'connect-source': [control: 'wallet_panel']
  'disconnect-source': []
  'connect-ton': []
  'disconnect-ton': []
}>()

const showPhantomDeeplinkButton = computed(
  () => props.sourceChainType === 'solana' && props.phantomDeeplinkAvailable
)

// On Solana mobile-deeplink the Phantom button is the only working entry
// point — non-Phantom Solana wallets are excluded from AppKit (see
// appkit.service.ts: NON_PHANTOM_SOLANA_WALLET_IDS) so an "Other Solana
// wallets" button just opens an empty/dead-end picker. Hide the fallback
// button in that environment. EVM users still get the AppKit picker.
const showFallbackSourceButton = computed(() => !showPhantomDeeplinkButton.value)

const sourceConnectLabel = computed(() => {
  if (props.isSourceBusy) return 'Connecting...'
  if (props.sourceChainType === 'evm') return 'Connect EVM Wallet'
  return 'Connect Solana Wallet'
})
</script>
